import React, { useState, useEffect } from 'react';
import { taskAPI, clientAPI } from '../api/api';
import { useAuth } from '../context/AuthContext';
import { useLocation } from 'react-router-dom';
import { DollarSign, Receipt, Download, Search, Building2, Calendar, Plus, Edit2, Percent, Printer, Eye, X, Banknote, MoreHorizontal, ExternalLink, RefreshCw } from 'lucide-react';
import { formatDate } from '../utils/dateFormat';

// Convert image URL to base64 for reliable print embedding
const getImageBase64 = (url) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      canvas.getContext('2d').drawImage(img, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => resolve('');
    img.src = url;
  });
};

// Number to words conversion for Indian numbering
const numberToWords = (num) => {
  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  
  if (num === 0) return 'Zero';
  
  const convertBelowThousand = (n) => {
    if (n === 0) return '';
    if (n < 20) return ones[n];
    if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 !== 0 ? ' ' + ones[n % 10] : '');
    return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 !== 0 ? ' ' + convertBelowThousand(n % 100) : '');
  };
  
  let result = '';
  const crore = Math.floor(num / 10000000);
  const lakh = Math.floor((num % 10000000) / 100000);
  const thousand = Math.floor((num % 100000) / 1000);
  const hundred = num % 1000;
  
  if (crore > 0) result += convertBelowThousand(crore) + ' Crore ';
  if (lakh > 0) result += convertBelowThousand(lakh) + ' Lakh ';
  if (thousand > 0) result += convertBelowThousand(thousand) + ' Thousand ';
  if (hundred > 0) result += convertBelowThousand(hundred);
  
  return result.trim() + ' Only';
};

const Billing = () => {
  const { isPartner, isManager } = useAuth();
  const location = useLocation();
  const [tasks, setTasks] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterClient, setFilterClient] = useState('');
  const [filterStatus, setFilterStatus] = useState('all'); // all, invoiced, paid, partial
  
  // Receipt Modal
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [selectedTaskForReceipt, setSelectedTaskForReceipt] = useState(null);
  const [selectedTaskObjForReceipt, setSelectedTaskObjForReceipt] = useState(null);
  const [receiptForm, setReceiptForm] = useState({ 
    number: '', 
    date: '', 
    amount: '', 
    mode: 'Bank',
    invoiceAmount: '',
    reimbursementAmount: ''
  });
  
  // Discount Modal
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [selectedTaskForDiscount, setSelectedTaskForDiscount] = useState(null);
  const [discountForm, setDiscountForm] = useState({ amount: '', reason: '', date: '' });
  
  // Edit Invoice Modal
  const [showEditInvoiceModal, setShowEditInvoiceModal] = useState(false);
  const [selectedTaskForEditInvoice, setSelectedTaskForEditInvoice] = useState(null);
  const [editInvoiceForm, setEditInvoiceForm] = useState({ number: '', date: '', amount: '' });

  // View Invoice Modal
  const [showViewInvoiceModal, setShowViewInvoiceModal] = useState(false);
  const [selectedTaskForView, setSelectedTaskForView] = useState(null);

  // Reimbursement Modal
  const [showReimbursementModal, setShowReimbursementModal] = useState(false);
  const [selectedTaskForReimbursement, setSelectedTaskForReimbursement] = useState(null);
  const [reimbursementForm, setReimbursementForm] = useState({ 
    type: 'Govt Fee', 
    description: '', 
    amount: '', 
    date: new Date().toISOString().split('T')[0],
    status: 'Pending'
  });

  // View Receipt Modal
  const [showViewReceiptModal, setShowViewReceiptModal] = useState(false);
  const [selectedTaskForReceiptView, setSelectedTaskForReceiptView] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    // Apply filter from URL query params
    const params = new URLSearchParams(location.search);
    const filter = params.get('filter');
    
    if (filter === 'completed') {
      setFilterStatus('all');
    } else if (filter === 'invoiced') {
      setFilterStatus('invoiced');
    } else if (filter === 'paid') {
      setFilterStatus('paid');
    }
  }, [location.search]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [tasksRes, clientsRes] = await Promise.all([
        taskAPI.getAll(), // Get all tasks
        clientAPI.getAll(),
      ]);
      // Show all tasks - billing can be done for any task
      setTasks(tasksRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to calculate balance
  const calculateBalance = (task) => {
    const invoiceAmount = task.invoice?.amount || 0;
    const discountAmount = task.discount?.amount || 0;
    const totalReceived = task.total_received || (task.receipt?.amount || 0);
    return invoiceAmount - discountAmount - totalReceived;
  };

  // Helper function to get total received
  const getTotalReceived = (task) => {
    return task.total_received || (task.receipt?.amount || 0);
  };

  // Filter tasks
  const filteredTasks = tasks.filter((task) => {
    // Search
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      const matchesSearch =
        task.client_name?.toLowerCase().includes(search) ||
        task.service_name?.toLowerCase().includes(search) ||
        task.task_type_name?.toLowerCase().includes(search) ||
        task.invoice?.number?.toLowerCase().includes(search);
      if (!matchesSearch) return false;
    }

    // Client filter
    if (filterClient && task.client_id !== filterClient) return false;

    // Status filter
    const balance = calculateBalance(task);
    if (filterStatus === 'invoiced' && (!task.invoice || getTotalReceived(task) > 0)) return false;
    if (filterStatus === 'paid' && balance !== 0) return false;  // Exactly zero = fully paid
    if (filterStatus === 'partial' && (!task.invoice || getTotalReceived(task) === 0 || balance <= 0)) return false;
    if (filterStatus === 'excess' && balance >= 0) return false;  // Negative = excess
    if (filterStatus === 'non-billable' && task.is_billable !== false) return false;  // Non-billable tasks
    if (filterStatus === 'reimbursements' && (!task.reimbursements || task.reimbursements.length === 0)) return false;  // Tasks with reimbursements

    return true;
  });

  // Calculate reimbursement summary
  const reimbursementSummary = tasks.reduce((acc, task) => {
    if (task.reimbursements && task.reimbursements.length > 0) {
      task.reimbursements.forEach(r => {
        acc.total += r.amount || 0;
        if (r.status === 'Collected') {
          acc.collected += r.amount || 0;
        } else {
          acc.pending += r.amount || 0;
        }
      });
    }
    return acc;
  }, { total: 0, collected: 0, pending: 0 });

  const exportToExcel = () => {
    const headers = [
      'Client',
      'Service',
      'Task Type',
      'Completed Date',
      'Invoice No',
      'Invoice Date',
      'Invoice Amount',
      'Receipt No',
      'Receipt Date',
      'Receipt Amount',
      'Receipt Mode',
      'Status',
    ];
    
    const rows = filteredTasks.map(task => [
      task.client_name,
      task.service_name,
      task.task_type_name,
      new Date(task.updated_at).toLocaleDateString(),
      task.invoice?.number || '-',
      task.invoice?.date ? new Date(task.invoice.date).toLocaleDateString() : '-',
      task.invoice?.amount || '-',
      task.receipt?.number || '-',
      task.receipt?.date ? new Date(task.receipt.date).toLocaleDateString() : '-',
      task.receipt?.amount || '-',
      task.receipt?.mode || '-',
      task.receipt ? 'Paid' : task.invoice ? 'Invoiced' : 'Completed',
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `billing-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  // Handle add receipt
  const handleAddReceipt = async (e) => {
    e.preventDefault();
    if (!receiptForm.number || !receiptForm.date || !receiptForm.amount || !receiptForm.mode) {
      alert('Please fill in all fields');
      return;
    }

    const totalAmount = parseFloat(receiptForm.amount) || 0;
    const invoiceAmt = parseFloat(receiptForm.invoiceAmount) || 0;
    const reimbAmt = parseFloat(receiptForm.reimbursementAmount) || 0;

    // Validate that invoice + reimbursement = total
    if (Math.abs((invoiceAmt + reimbAmt) - totalAmount) > 0.01) {
      alert('Invoice Amount + Reimbursement Amount must equal Total Amount');
      return;
    }

    try {
      // Add receipt with breakdown info
      await taskAPI.addReceipt(selectedTaskForReceipt, {
        number: receiptForm.number,
        date: receiptForm.date,
        amount: totalAmount,
        mode: receiptForm.mode,
        invoice_amount: invoiceAmt,
        reimbursement_amount: reimbAmt
      });

      // If reimbursement amount is specified, mark pending reimbursements as collected
      if (reimbAmt > 0 && selectedTaskObjForReceipt?.reimbursements) {
        let remainingReimbAmount = reimbAmt;
        for (const reimb of selectedTaskObjForReceipt.reimbursements) {
          if (reimb.status === 'Pending' && remainingReimbAmount > 0) {
            if (remainingReimbAmount >= reimb.amount) {
              // Collect this reimbursement fully
              await taskAPI.collectReimbursement(selectedTaskForReceipt, reimb.id);
              remainingReimbAmount -= reimb.amount;
            }
          }
        }
      }

      setShowReceiptModal(false);
      setReceiptForm({ number: '', date: '', amount: '', mode: 'Bank', invoiceAmount: '', reimbursementAmount: '' });
      setSelectedTaskForReceipt(null);
      setSelectedTaskObjForReceipt(null);
      fetchData();
    } catch (error) {
      alert('Failed to add receipt');
    }
  };

  // Handle add discount
  const handleAddDiscount = async (e) => {
    e.preventDefault();
    if (!discountForm.amount || !discountForm.date) {
      alert('Please fill in amount and date');
      return;
    }

    try {
      await taskAPI.addDiscount(selectedTaskForDiscount, {
        ...discountForm,
        amount: parseFloat(discountForm.amount),
      });
      setShowDiscountModal(false);
      setDiscountForm({ amount: '', reason: '', date: '' });
      setSelectedTaskForDiscount(null);
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to add discount');
    }
  };

  // Handle add reimbursement
  const handleAddReimbursement = async (e) => {
    e.preventDefault();
    if (!reimbursementForm.type || !reimbursementForm.amount || !reimbursementForm.date) {
      alert('Please fill in type, amount and date');
      return;
    }

    try {
      await taskAPI.addReimbursement(selectedTaskForReimbursement, {
        ...reimbursementForm,
        amount: parseFloat(reimbursementForm.amount),
      });
      setShowReimbursementModal(false);
      setReimbursementForm({ type: 'Govt Fee', description: '', amount: '', date: new Date().toISOString().split('T')[0], status: 'Pending' });
      setSelectedTaskForReimbursement(null);
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to add reimbursement');
    }
  };

  // Open reimbursement modal
  const openReimbursementModal = (taskId) => {
    setSelectedTaskForReimbursement(taskId);
    setReimbursementForm({ 
      type: 'Govt Fee', 
      description: '', 
      amount: '', 
      date: new Date().toISOString().split('T')[0],
      status: 'Pending'
    });
    setShowReimbursementModal(true);
  };

  // Open receipt view modal
  const openReceiptViewModal = (task) => {
    setSelectedTaskForReceiptView(task);
    setShowViewReceiptModal(true);
  };

  // Print receipt
  const handlePrintReceipt = async () => {
    if (!selectedTaskForReceiptView) return;
    
    const task = selectedTaskForReceiptView;
    const client = clients.find(c => c.id === task.client_id);
    const receipts = task.receipts || [];
    const totalReceived = receipts.reduce((sum, r) => sum + (r.amount || 0), 0);
    const logoBase64 = await getImageBase64(window.location.origin + '/images/ca-logo.png');
    
    // Generate receipt rows
    let receiptRows = '';
    receipts.forEach((r, idx) => {
      const receiptDate = r.date ? new Date(r.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' }).replace(/ /g, '-') : '-';
      receiptRows += `
        <tr>
          <td>${idx + 1}</td>
          <td>${receiptDate}</td>
          <td>${r.number || '-'}</td>
          <td>₹${(r.amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
          <td>${r.mode || '-'}</td>
        </tr>
      `;
    });
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Receipt - ${client?.name || task.client_name}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: 'Segoe UI', Arial, sans-serif; padding: 30px; font-size: 14px; }
            .receipt-container { max-width: 700px; margin: 0 auto; border: 2px solid #000; padding: 20px; }
            .header { text-align: center; margin-bottom: 20px; border-bottom: 2px solid #000; padding-bottom: 15px; }
            .company-name { font-size: 24px; font-weight: bold; color: #1a365d; margin-bottom: 5px; }
            .company-title { font-size: 14px; font-weight: 500; color: #333; }
            .company-address { font-size: 11px; color: #555; margin-top: 5px; }
            .receipt-title { text-align: center; font-size: 20px; font-weight: bold; margin: 20px 0; text-decoration: underline; }
            .info-section { display: flex; justify-content: space-between; margin-bottom: 20px; }
            .info-box { }
            .info-label { font-size: 12px; color: #666; }
            .info-value { font-size: 14px; font-weight: bold; }
            .party-section { margin-bottom: 20px; padding: 10px; background: #f5f5f5; border-radius: 5px; }
            .party-label { font-size: 12px; color: #666; }
            .party-name { font-size: 18px; font-weight: bold; }
            .invoice-ref { font-size: 12px; color: #666; margin-top: 5px; }
            .receipt-table { width: 100%; border-collapse: collapse; margin: 20px 0; }
            .receipt-table th, .receipt-table td { padding: 10px; text-align: left; border: 1px solid #ddd; }
            .receipt-table th { background: #f0f0f0; font-weight: 600; }
            .total-section { text-align: right; margin: 20px 0; padding: 15px; background: #e8f5e9; border-radius: 5px; }
            .total-label { font-size: 14px; }
            .total-amount { font-size: 24px; font-weight: bold; color: #2e7d32; }
            .amount-words { font-style: italic; font-size: 12px; margin-top: 5px; }
            .thanks-section { text-align: center; margin-top: 30px; padding: 15px; background: #fff3e0; border-radius: 5px; }
            .thanks-text { font-size: 16px; font-weight: 500; color: #e65100; }
            .footer { margin-top: 30px; display: flex; justify-content: space-between; }
            .signature-box { text-align: right; }
            .signature-line { border-top: 1px solid #000; width: 200px; margin-left: auto; padding-top: 5px; }
            @media print { 
              body { padding: 10px; } 
              .receipt-container { border: 1px solid #000; }
            }
          </style>
        </head>
        <body>
          <div class="receipt-container">
            <div class="header">
              ${logoBase64 ? `<img src="${logoBase64}" alt="CA" style="height: 50px; margin-bottom: 8px;" />` : ''}
              <div class="company-name">HYDER ALI AND ASSOCIATES</div>
              <div class="company-title">CHARTERED ACCOUNTANTS</div>
              <div class="company-address">XI/405,406,407, Sahara Building, Kadappadi, Peruvallur, Kondotty Malappuram, Kerala- 673638</div>
              <div class="company-address">E-Mail: cahyderali@gmail.com</div>
            </div>
            
            <div class="receipt-title">PAYMENT RECEIPT</div>
            
            <div class="info-section">
              <div class="info-box">
                <div class="info-label">Date:</div>
                <div class="info-value">${new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</div>
              </div>
              ${task.invoice ? `
              <div class="info-box">
                <div class="info-label">Invoice Reference:</div>
                <div class="info-value">${task.invoice.number || '-'}</div>
              </div>
              ` : ''}
            </div>
            
            <div class="party-section">
              <div class="party-label">Received From:</div>
              <div class="party-name">${client?.name || task.client_name || '-'}</div>
              <div class="invoice-ref">Service: ${task.service_name} - ${task.task_type_name || ''}</div>
            </div>
            
            <table class="receipt-table">
              <thead>
                <tr>
                  <th style="width: 50px;">SI No.</th>
                  <th>Date</th>
                  <th>Receipt No.</th>
                  <th>Amount</th>
                  <th>Mode</th>
                </tr>
              </thead>
              <tbody>
                ${receiptRows}
              </tbody>
            </table>
            
            <div class="total-section">
              <div class="total-label">Total Amount Received:</div>
              <div class="total-amount">₹${totalReceived.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
              <div class="amount-words">INR ${numberToWords(Math.round(totalReceived))}</div>
            </div>
            
            <div class="thanks-section">
              <div class="thanks-text">Thank you for your payment!</div>
            </div>
            
            <div class="footer">
              <div></div>
              <div class="signature-box">
                <div class="signature-line">Authorised Signatory</div>
              </div>
            </div>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    setTimeout(() => printWindow.print(), 300);
  };

  // Handle edit invoice
  const handleEditInvoice = async (e) => {
    e.preventDefault();
    if (!editInvoiceForm.number || !editInvoiceForm.date || !editInvoiceForm.amount) {
      alert('Please fill in all fields');
      return;
    }

    try {
      await taskAPI.editInvoice(selectedTaskForEditInvoice, {
        ...editInvoiceForm,
        amount: parseFloat(editInvoiceForm.amount),
      });
      setShowEditInvoiceModal(false);
      setEditInvoiceForm({ number: '', date: '', amount: '' });
      setSelectedTaskForEditInvoice(null);
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to edit invoice');
    }
  };

  // Open edit invoice modal
  const openEditInvoiceModal = (task) => {
    setSelectedTaskForEditInvoice(task.id);
    setEditInvoiceForm({
      number: task.invoice?.number || '',
      date: task.invoice?.date?.split('T')[0] || '',
      amount: task.invoice?.amount?.toString() || '',
    });
    setShowEditInvoiceModal(true);
  };

  // View invoice
  const openViewInvoiceModal = (task) => {
    setSelectedTaskForView(task);
    setShowViewInvoiceModal(true);
  };

  // Print invoice - Voucher Style
  const handlePrintInvoice = async () => {
    if (!selectedTaskForView) return;
    
    const task = selectedTaskForView;
    const logoBase64 = await getImageBase64(window.location.origin + '/images/ca-logo.png');
    const client = clients.find(c => c.id === task.client_id);
    const invoiceDate = task.invoice?.date ? new Date(task.invoice.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';
    const serviceAmount = task.invoice?.amount || 0;
    
    // Calculate reimbursements total
    const reimbursements = task.reimbursements || [];
    const reimbTotal = reimbursements.reduce((sum, r) => sum + (r.amount || 0), 0);
    const grandTotal = serviceAmount + reimbTotal;
    const amountInWords = 'INR ' + numberToWords(Math.round(grandTotal));
    
    // Generate reimbursement rows
    let reimbRows = '';
    reimbursements.forEach((r, idx) => {
      reimbRows += `
        <tr class="reimb-row">
          <td>${idx + 2}</td>
          <td>${r.type}${r.description ? ' - ' + r.description : ''}</td>
          <td class="amount">₹${(r.amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
        </tr>
      `;
    });

    // Subtotals section
    let subtotalsHtml = '';
    if (reimbTotal > 0) {
      subtotalsHtml = `
        <div class="subtotals">
          <div class="subtotal-row">
            <span class="label">Professional Fees:</span>
            <span class="value">₹${serviceAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
          </div>
          <div class="subtotal-row">
            <span class="label">Reimbursements (Tax/Govt Fees):</span>
            <span class="value">₹${reimbTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
          </div>
        </div>
      `;
    }
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Invoice ${task.invoice?.number || ''}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: 'Segoe UI', Arial, sans-serif; padding: 30px; font-size: 14px; }
            .invoice-container { max-width: 700px; margin: 0 auto; border: 2px solid #000; padding: 25px; }
            .header { text-align: center; border-bottom: 2px solid #000; padding-bottom: 15px; margin-bottom: 15px; }
            .company-name { font-size: 24px; font-weight: bold; color: #1a365d; margin-bottom: 5px; }
            .company-title { font-size: 14px; font-weight: 500; color: #333; }
            .company-address { font-size: 11px; color: #555; margin-top: 5px; }
            .invoice-title { text-align: center; font-size: 20px; font-weight: bold; margin: 20px 0; text-decoration: underline; color: #1e40af; }
            .info-section { display: flex; justify-content: space-between; margin-bottom: 20px; }
            .info-box { }
            .info-label { font-size: 12px; color: #666; }
            .info-value { font-size: 16px; font-weight: bold; }
            .party-section { background: #f5f5f5; padding: 12px; border-radius: 5px; margin-bottom: 20px; }
            .party-label { font-size: 12px; color: #666; }
            .party-name { font-size: 18px; font-weight: bold; }
            .service-info { font-size: 12px; color: #666; margin-top: 5px; }
            .items-table { width: 100%; border-collapse: collapse; margin-bottom: 15px; }
            .items-table th, .items-table td { padding: 10px; text-align: left; border: 1px solid #ddd; }
            .items-table th { background: #f0f0f0; font-weight: 600; font-size: 12px; }
            .items-table .amount { text-align: right; }
            .items-table .reimb-row { background: #fffbeb; }
            .subtotals { margin-bottom: 15px; }
            .subtotal-row { display: flex; justify-content: space-between; padding: 5px 0; border-bottom: 1px dashed #ddd; font-size: 13px; }
            .subtotal-row .label { color: #666; }
            .subtotal-row .value { font-weight: 500; }
            .total-section { background: #eff6ff; padding: 15px; border-radius: 5px; text-align: right; margin-bottom: 15px; }
            .total-label { font-size: 14px; color: #666; }
            .total-amount { font-size: 24px; font-weight: bold; color: #1e40af; }
            .amount-words { font-style: italic; font-size: 12px; color: #666; margin-top: 5px; }
            .bank-section { background: #f5f5f5; padding: 12px; border-radius: 5px; margin-bottom: 20px; font-size: 12px; }
            .bank-title { font-weight: bold; margin-bottom: 8px; }
            .bank-grid { display: grid; grid-template-columns: 120px 1fr; gap: 3px; }
            .bank-label { color: #666; }
            .footer { display: flex; justify-content: space-between; align-items: flex-end; margin-top: 30px; }
            .eoe { font-size: 11px; color: #999; }
            .signature-box { text-align: right; }
            .signature-company { font-size: 13px; font-weight: 500; }
            .signature-line { border-top: 1px solid #000; width: 180px; margin-left: auto; padding-top: 5px; margin-top: 50px; font-size: 12px; }
            @media print { 
              body { padding: 10px; } 
              .invoice-container { border: 1px solid #000; }
            }
          </style>
        </head>
        <body>
          <div class="invoice-container">
            <div class="header">
              ${logoBase64 ? `<img src="${logoBase64}" alt="CA" style="height: 50px; margin-bottom: 8px;" />` : ''}
              <div class="company-name">HYDER ALI AND ASSOCIATES</div>
              <div class="company-title">CHARTERED ACCOUNTANTS</div>
              <div class="company-address">XI/405,406,407, Sahara Building, Kadappadi, Peruvallur, Kondotty Malappuram, Kerala- 673638</div>
              <div class="company-address">E-Mail: cahyderali@gmail.com</div>
            </div>
            
            <div class="invoice-title">INVOICE</div>
            
            <div class="info-section">
              <div class="info-box">
                <div class="info-label">Invoice No:</div>
                <div class="info-value">${task.invoice?.number || '-'}</div>
              </div>
              <div class="info-box">
                <div class="info-label">Date:</div>
                <div class="info-value">${invoiceDate}</div>
              </div>
            </div>
            
            <div class="party-section">
              <div class="party-label">Bill To:</div>
              <div class="party-name">${client?.name || task.client_name || '-'}</div>
              <div class="service-info">Service: ${task.service_name} - ${task.task_type_name || ''}</div>
            </div>
            
            <table class="items-table">
              <thead>
                <tr>
                  <th style="width: 50px;">SI No.</th>
                  <th>Description</th>
                  <th class="amount" style="width: 110px;">Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>1</td>
                  <td>${task.invoice?.description || task.service_name + ' - ' + (task.task_type_name || '')}</td>
                  <td class="amount">₹${serviceAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                </tr>
                ${reimbRows}
              </tbody>
            </table>
            
            ${subtotalsHtml}
            
            <div class="total-section">
              <div class="total-label">Grand Total:</div>
              <div class="total-amount">₹${grandTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
              <div class="amount-words">${amountInWords}</div>
            </div>
            
            <div class="bank-section">
              <div class="bank-title">Bank Details:</div>
              <div class="bank-grid">
                <span class="bank-label">Bank Name:</span>
                <span>BANK OF BARODA</span>
                <span class="bank-label">A/c No.:</span>
                <span>40900200000273</span>
                <span class="bank-label">Branch & IFS Code:</span>
                <span>KOTTAKKAL & BARBOKOTTAK</span>
              </div>
            </div>
            
            <div class="footer">
              <div class="eoe">E. & O.E</div>
              <div class="signature-box">
                <div class="signature-company">for HYDER ALI AND ASSOCIATES</div>
                <div class="signature-line">Authorised Signatory</div>
              </div>
            </div>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    setTimeout(() => printWindow.print(), 300);
  };

  // Calculate summary
  const summary = {
    totalInvoiced: filteredTasks.reduce((sum, t) => sum + (t.invoice?.amount || 0), 0),
    totalReceived: filteredTasks.reduce((sum, t) => sum + getTotalReceived(t), 0),
    totalDiscount: filteredTasks.reduce((sum, t) => sum + (t.discount?.amount || 0), 0),
    pending: filteredTasks.filter(t => t.invoice && calculateBalance(t) > 0).length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Billing & Invoices</h2>
        <button
          onClick={exportToExcel}
          className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center"
        >
          <Download className="w-4 h-4 mr-2" />
          Export
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white p-4 rounded-lg shadow border">
          <div className="flex items-center space-x-3">
            <div className="bg-blue-100 p-3 rounded-lg">
              <DollarSign className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Invoiced</p>
              <p className="text-2xl font-bold text-blue-600">₹{summary.totalInvoiced.toLocaleString()}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border">
          <div className="flex items-center space-x-3">
            <div className="bg-green-100 p-3 rounded-lg">
              <Receipt className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Received</p>
              <p className="text-2xl font-bold text-green-600">₹{summary.totalReceived.toLocaleString()}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border">
          <div className="flex items-center space-x-3">
            <div className="bg-red-100 p-3 rounded-lg">
              <DollarSign className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Pending Payment</p>
              <p className="text-2xl font-bold text-red-600">₹{(summary.totalInvoiced - summary.totalReceived).toLocaleString()}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-6 rounded-lg shadow border">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">
              <Search className="w-4 h-4 inline mr-2" />
              Search
            </label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search client, service, task..."
              className="w-full px-3 py-2 border rounded-lg"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Client</label>
            <select
              value={filterClient}
              onChange={(e) => setFilterClient(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg"
            >
              <option value="">All Clients</option>
              {clients.map((client) => (
                <option key={client.id} value={client.id}>
                  {client.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Payment Status</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg"
            >
              <option value="all">All</option>
              <option value="invoiced">Invoiced (No Payment)</option>
              <option value="partial">Partial Payment</option>
              <option value="paid">Fully Paid</option>
              <option value="excess">Excess Payment</option>
              <option value="non-billable">Non-billable Tasks</option>
              <option value="reimbursements">With Reimbursements</option>
            </select>
          </div>
        </div>
        <p className="text-sm text-gray-600 mt-4">
          Showing {filteredTasks.length} of {tasks.length} tasks | 
          <span className="ml-2 text-red-600">Discount: ₹{summary.totalDiscount.toLocaleString()}</span>
          {reimbursementSummary.total > 0 && (
            <span className="ml-4 text-amber-600">
              Reimbursements: ₹{reimbursementSummary.pending.toLocaleString()} pending / ₹{reimbursementSummary.collected.toLocaleString()} collected
            </span>
          )}
        </p>
      </div>

      {/* Tasks Table */}
      <div className="bg-white rounded-lg shadow border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Service</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invoice</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Discount</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Received</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Balance</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reimb.</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filteredTasks.map((task) => {
                const balance = calculateBalance(task);
                const totalReceived = getTotalReceived(task);
                const isPaid = task.invoice && balance === 0;  // Exactly zero = fully paid
                const isExcess = task.invoice && balance < 0;  // Negative = overpaid
                const isPartial = task.invoice && totalReceived > 0 && balance > 0;  // Some payment but not full
                const isNonBillable = task.is_billable === false;
                const reimbTotal = task.reimbursements?.reduce((sum, r) => sum + (r.amount || 0), 0) || 0;
                const reimbPending = task.reimbursements?.filter(r => r.status !== 'Collected').reduce((sum, r) => sum + (r.amount || 0), 0) || 0;
                
                return (
                  <tr key={task.id} className={`hover:bg-gray-50 ${isNonBillable ? 'bg-gray-50' : ''}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center">
                        <Building2 className="w-4 h-4 mr-2 text-gray-400" />
                        <span className="font-medium">{task.client_name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div>{task.service_name}</div>
                      <div className="text-xs text-gray-500">{task.task_type_name}</div>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {task.invoice ? (
                        <div>
                          <div className="font-medium">{task.invoice.number}</div>
                          <div className="text-xs text-gray-500">
                            {formatDate(task.invoice.date)}
                          </div>
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {task.invoice ? (
                        <span className="font-medium text-blue-600">
                          ₹{task.invoice.amount.toLocaleString()}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {task.discount ? (
                        <div>
                          <span className="text-red-600">-₹{task.discount.amount.toLocaleString()}</span>
                          {task.discount.reason && (
                            <div className="text-xs text-gray-500">{task.discount.reason}</div>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {totalReceived > 0 ? (
                        <div>
                          <span className="text-green-600 font-medium">₹{totalReceived.toLocaleString()}</span>
                          {task.receipts && task.receipts.length > 1 && (
                            <div className="text-xs text-gray-500">({task.receipts.length} receipts)</div>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {task.invoice ? (
                        <span className={`font-semibold ${balance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                          ₹{Math.abs(balance).toLocaleString()}
                          {balance < 0 && ' (Excess)'}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    {/* Reimbursements Column */}
                    <td className="px-4 py-3 text-sm">
                      {reimbTotal > 0 ? (
                        <div>
                          <span className="text-amber-600 font-medium">₹{reimbTotal.toLocaleString()}</span>
                          {reimbPending > 0 && (
                            <div className="text-xs text-red-500">₹{reimbPending.toLocaleString()} pending</div>
                          )}
                          {reimbPending === 0 && (
                            <div className="text-xs text-green-500">All collected</div>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {isNonBillable ? (
                        <span className="px-2 py-1 text-xs rounded bg-gray-200 text-gray-600">
                          Non-billable
                        </span>
                      ) : isPaid ? (
                        <span className="px-2 py-1 text-xs rounded bg-green-100 text-green-700">
                          Fully Paid
                        </span>
                      ) : isExcess ? (
                        <span className="px-2 py-1 text-xs rounded bg-blue-100 text-blue-700">
                          Excess
                        </span>
                      ) : isPartial ? (
                        <span className="px-2 py-1 text-xs rounded bg-orange-100 text-orange-700">
                          Partial
                        </span>
                      ) : task.invoice ? (
                        <span className="px-2 py-1 text-xs rounded bg-yellow-100 text-yellow-700">
                          Pending
                        </span>
                      ) : (
                        <span className="px-2 py-1 text-xs rounded bg-gray-100 text-gray-700">
                          Completed
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center space-x-1">
                        {/* View/Print Invoice */}
                        {task.invoice && (
                          <button
                            onClick={() => openViewInvoiceModal(task)}
                            className="p-1.5 text-purple-600 hover:bg-purple-100 rounded"
                            data-testid="view-invoice-button"
                            title="View Invoice"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        )}
                        {/* Edit Invoice - Partner only */}
                        {task.invoice && isPartner && (
                          <button
                            onClick={() => openEditInvoiceModal(task)}
                            className="p-1.5 text-blue-600 hover:bg-blue-100 rounded"
                            data-testid="edit-invoice-button"
                            title="Edit Invoice"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                        )}
                        {/* Payment Link - Show if link exists */}
                        {task.payment_link && (
                          <button
                            onClick={() => {
                              navigator.clipboard?.writeText(task.payment_link);
                              window.open(task.payment_link, '_blank');
                            }}
                            className={`p-1.5 rounded text-xs font-medium ${
                              task.payment_status === 'paid' 
                                ? 'text-green-600 hover:bg-green-100' 
                                : 'text-orange-600 hover:bg-orange-100'
                            }`}
                            data-testid="payment-link-button"
                            title={`Payment Link (${task.payment_status || 'pending'})\nClick to open & copy`}
                          >
                            <ExternalLink className="w-4 h-4" />
                          </button>
                        )}
                        {/* Regenerate Payment Link */}
                        {task.invoice && isPartner && (
                          <button
                            onClick={async () => {
                              try {
                                const res = await taskAPI.regeneratePaymentLink(task.id);
                                alert(`New live payment link created!\n${res.data.payment_link}`);
                                fetchData();
                              } catch (err) {
                                alert(err.response?.data?.detail || 'Failed to regenerate');
                              }
                            }}
                            className="p-1.5 rounded text-xs font-medium text-blue-600 hover:bg-blue-100"
                            data-testid="regenerate-payment-link"
                            title="Regenerate Payment Link (Live)"
                          >
                            <RefreshCw className="w-4 h-4" />
                          </button>
                        )}
                        {/* Add Receipt - Show if:
                            1. Invoice exists AND (balance > 0 OR pending reimbursements)
                            2. OR Non-billable with pending reimbursements */}
                        {(isPartner || isManager) && (
                          (task.invoice && (balance > 0 || (task.reimbursements && task.reimbursements.some(r => r.status === 'Pending')))) ||
                          (!task.invoice && task.is_billable === false && task.reimbursements && task.reimbursements.some(r => r.status === 'Pending'))
                        ) && (
                          <button
                            onClick={() => {
                              setSelectedTaskForReceipt(task.id);
                              setSelectedTaskObjForReceipt(task);
                              // Calculate pending amounts
                              const pendingReimb = task.reimbursements?.filter(r => r.status === 'Pending').reduce((sum, r) => sum + (r.amount || 0), 0) || 0;
                              const invoiceBalance = balance > 0 ? balance : 0;
                              setReceiptForm({ 
                                number: '', 
                                date: new Date().toISOString().split('T')[0], 
                                amount: (invoiceBalance + pendingReimb).toString(),
                                mode: 'Bank',
                                invoiceAmount: invoiceBalance > 0 ? invoiceBalance.toString() : '0',
                                reimbursementAmount: pendingReimb > 0 ? pendingReimb.toString() : '0'
                              });
                              setShowReceiptModal(true);
                            }}
                            className="p-1.5 bg-green-600 text-white hover:bg-green-700 rounded"
                            data-testid="add-receipt-button"
                            title="Add Receipt"
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        )}
                        {/* View/Print Receipt - Show if receipts exist */}
                        {task.receipts && task.receipts.length > 0 && (
                          <button
                            onClick={() => openReceiptViewModal(task)}
                            className="p-1.5 text-teal-600 hover:bg-teal-100 rounded"
                            data-testid="view-receipt-button"
                            title="View Receipt"
                          >
                            <Receipt className="w-4 h-4" />
                          </button>
                        )}
                        {/* Add Reimbursement - Show for invoiced tasks OR non-billable tasks */}
                        {(isPartner || isManager) && (task.invoice || task.is_billable === false) && (
                          <button
                            onClick={() => openReimbursementModal(task.id)}
                            className="p-1.5 text-amber-600 hover:bg-amber-100 rounded"
                            data-testid="add-reimbursement-button"
                            title="Add Reimbursement"
                          >
                            <Banknote className="w-4 h-4" />
                          </button>
                        )}
                        {/* Add Discount - Show if invoice exists and no discount yet */}
                        {task.invoice && !task.discount && (isPartner || isManager) && (
                          <button
                            onClick={() => {
                              setSelectedTaskForDiscount(task.id);
                              setDiscountForm({ 
                                amount: '', 
                                reason: '',
                                date: new Date().toISOString().split('T')[0]
                              });
                              setShowDiscountModal(true);
                            }}
                            className="p-1.5 text-orange-600 hover:bg-orange-100 rounded"
                            data-testid="add-discount-button"
                            title="Add Discount"
                          >
                            <Percent className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {filteredTasks.length === 0 && (
          <div className="text-center py-12">
            <DollarSign className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No billing records found.</p>
          </div>
        )}
      </div>

      {/* Receipt Modal */}
      {showReceiptModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-lg">
            <h3 className="text-lg font-semibold mb-4">Add Receipt</h3>
            
            {/* Summary of pending amounts */}
            {selectedTaskObjForReceipt && (
              <div className="bg-gray-50 p-3 rounded-lg mb-4 text-sm">
                <p className="font-medium text-gray-700 mb-2">Client: {selectedTaskObjForReceipt.client_name}</p>
                <div className="grid grid-cols-2 gap-2">
                  {selectedTaskObjForReceipt.invoice && (
                    <>
                      <span className="text-gray-600">Invoice Amount:</span>
                      <span className="font-medium">₹{selectedTaskObjForReceipt.invoice.amount?.toLocaleString()}</span>
                      <span className="text-gray-600">Already Received:</span>
                      <span className="font-medium text-green-600">₹{(selectedTaskObjForReceipt.total_received || 0).toLocaleString()}</span>
                      <span className="text-gray-600">Invoice Balance:</span>
                      <span className="font-medium text-blue-600">
                        ₹{Math.max(0, (selectedTaskObjForReceipt.invoice.amount - (selectedTaskObjForReceipt.total_received || 0) - (selectedTaskObjForReceipt.discount?.amount || 0))).toLocaleString()}
                      </span>
                    </>
                  )}
                  {selectedTaskObjForReceipt.reimbursements?.some(r => r.status === 'Pending') && (
                    <>
                      <span className="text-gray-600">Pending Reimbursements:</span>
                      <span className="font-medium text-amber-600">
                        ₹{selectedTaskObjForReceipt.reimbursements.filter(r => r.status === 'Pending').reduce((sum, r) => sum + (r.amount || 0), 0).toLocaleString()}
                      </span>
                    </>
                  )}
                </div>
              </div>
            )}
            
            <form onSubmit={handleAddReceipt} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Receipt Number *</label>
                  <input
                    type="text"
                    value={receiptForm.number}
                    onChange={(e) => setReceiptForm({ ...receiptForm, number: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="e.g., REC-001"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Receipt Date *</label>
                  <input
                    type="date"
                    value={receiptForm.date}
                    onChange={(e) => setReceiptForm({ ...receiptForm, date: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                    required
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Total Amount Received *</label>
                <input
                  type="number"
                  step="0.01"
                  value={receiptForm.amount}
                  onChange={(e) => {
                    const total = parseFloat(e.target.value) || 0;
                    const invBal = selectedTaskObjForReceipt?.invoice 
                      ? Math.max(0, selectedTaskObjForReceipt.invoice.amount - (selectedTaskObjForReceipt.total_received || 0) - (selectedTaskObjForReceipt.discount?.amount || 0))
                      : 0;
                    const reimbPending = selectedTaskObjForReceipt?.reimbursements?.filter(r => r.status === 'Pending').reduce((sum, r) => sum + (r.amount || 0), 0) || 0;
                    
                    // Auto-distribute: first to invoice balance, then to reimbursements
                    let invoiceAmt = Math.min(total, invBal);
                    let reimbAmt = Math.min(total - invoiceAmt, reimbPending);
                    
                    setReceiptForm({ 
                      ...receiptForm, 
                      amount: e.target.value,
                      invoiceAmount: invoiceAmt.toString(),
                      reimbursementAmount: reimbAmt.toString()
                    });
                  }}
                  className="w-full px-3 py-2 border rounded-lg text-lg font-medium"
                  placeholder="Enter total amount"
                  required
                />
              </div>

              {/* Amount Breakdown */}
              <div className="bg-blue-50 p-3 rounded-lg">
                <p className="text-sm font-medium text-blue-800 mb-2">Amount Breakdown</p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Towards Invoice</label>
                    <input
                      type="number"
                      step="0.01"
                      value={receiptForm.invoiceAmount}
                      onChange={(e) => {
                        const invAmt = parseFloat(e.target.value) || 0;
                        const total = parseFloat(receiptForm.amount) || 0;
                        setReceiptForm({ 
                          ...receiptForm, 
                          invoiceAmount: e.target.value,
                          reimbursementAmount: Math.max(0, total - invAmt).toString()
                        });
                      }}
                      className="w-full px-3 py-2 border rounded-lg bg-white"
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Towards Reimbursement</label>
                    <input
                      type="number"
                      step="0.01"
                      value={receiptForm.reimbursementAmount}
                      onChange={(e) => {
                        const reimbAmt = parseFloat(e.target.value) || 0;
                        const total = parseFloat(receiptForm.amount) || 0;
                        setReceiptForm({ 
                          ...receiptForm, 
                          reimbursementAmount: e.target.value,
                          invoiceAmount: Math.max(0, total - reimbAmt).toString()
                        });
                      }}
                      className="w-full px-3 py-2 border rounded-lg bg-white"
                      placeholder="0"
                    />
                  </div>
                </div>
                {parseFloat(receiptForm.amount) > 0 && (
                  <div className="mt-2 text-xs text-gray-600">
                    Total: ₹{(parseFloat(receiptForm.invoiceAmount) || 0) + (parseFloat(receiptForm.reimbursementAmount) || 0)} 
                    {Math.abs(((parseFloat(receiptForm.invoiceAmount) || 0) + (parseFloat(receiptForm.reimbursementAmount) || 0)) - (parseFloat(receiptForm.amount) || 0)) > 0.01 && (
                      <span className="text-red-600 ml-2">⚠ Must equal {receiptForm.amount}</span>
                    )}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Payment Mode *</label>
                <select
                  value={receiptForm.mode}
                  onChange={(e) => setReceiptForm({ ...receiptForm, mode: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  required
                >
                  <option value="Cash">Cash</option>
                  <option value="Bank">Bank Transfer</option>
                  <option value="UPI">UPI</option>
                  <option value="Cheque">Cheque</option>
                </select>
              </div>
              <div className="flex space-x-2">
                <button
                  type="submit"
                  className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
                >
                  Save Receipt
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowReceiptModal(false);
                    setReceiptForm({ number: '', date: '', amount: '', mode: 'Bank', invoiceAmount: '', reimbursementAmount: '' });
                    setSelectedTaskForReceipt(null);
                    setSelectedTaskObjForReceipt(null);
                  }}
                  className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Discount Modal */}
      {showDiscountModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Add Discount</h3>
            <form onSubmit={handleAddDiscount} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Discount Amount *</label>
                <input
                  type="number"
                  step="0.01"
                  value={discountForm.amount}
                  onChange={(e) => setDiscountForm({ ...discountForm, amount: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="Enter discount amount"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Reason</label>
                <input
                  type="text"
                  value={discountForm.reason}
                  onChange={(e) => setDiscountForm({ ...discountForm, reason: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="e.g., Early payment, Loyal customer"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Date *</label>
                <input
                  type="date"
                  value={discountForm.date}
                  onChange={(e) => setDiscountForm({ ...discountForm, date: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  required
                />
              </div>
              <div className="flex space-x-2">
                <button
                  type="submit"
                  className="bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700"
                >
                  Apply Discount
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowDiscountModal(false);
                    setDiscountForm({ amount: '', reason: '', date: '' });
                    setSelectedTaskForDiscount(null);
                  }}
                  className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Invoice Modal - Partner Only */}
      {showEditInvoiceModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Edit Invoice</h3>
            <form onSubmit={handleEditInvoice} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Invoice Number *</label>
                <input
                  type="text"
                  value={editInvoiceForm.number}
                  onChange={(e) => setEditInvoiceForm({ ...editInvoiceForm, number: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="e.g., INV-001"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Invoice Date *</label>
                <input
                  type="date"
                  value={editInvoiceForm.date}
                  onChange={(e) => setEditInvoiceForm({ ...editInvoiceForm, date: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Amount *</label>
                <input
                  type="number"
                  step="0.01"
                  value={editInvoiceForm.amount}
                  onChange={(e) => setEditInvoiceForm({ ...editInvoiceForm, amount: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="Enter amount"
                  required
                />
              </div>
              <div className="flex space-x-2">
                <button
                  type="submit"
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                >
                  Update Invoice
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowEditInvoiceModal(false);
                    setEditInvoiceForm({ number: '', date: '', amount: '' });
                    setSelectedTaskForEditInvoice(null);
                  }}
                  className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Invoice Modal */}
      {showViewInvoiceModal && selectedTaskForView && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b bg-gray-50 sticky top-0">
              <h3 className="text-lg font-semibold flex items-center">
                <Receipt className="w-5 h-5 mr-2 text-blue-600" />
                Invoice
              </h3>
              <div className="flex items-center space-x-2">
                <button
                  onClick={handlePrintInvoice}
                  className="flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <Printer className="w-4 h-4 mr-2" />
                  Print Invoice
                </button>
                <button
                  onClick={() => {
                    setShowViewInvoiceModal(false);
                    setSelectedTaskForView(null);
                  }}
                  className="text-gray-500 hover:text-gray-700 p-2"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Invoice Preview - Voucher Style */}
            <div className="p-6">
              <div className="border-2 border-gray-300 rounded-lg p-6">
                {/* Header - Centered like Receipt */}
                <div className="text-center border-b pb-4 mb-4">
                  <h2 className="text-xl font-bold text-blue-900">HYDER ALI AND ASSOCIATES</h2>
                  <p className="text-sm font-medium text-gray-700">CHARTERED ACCOUNTANTS</p>
                  <p className="text-xs text-gray-500">XI/405,406,407, Sahara Building, Kadappadi, Peruvallur, Kondotty Malappuram, Kerala- 673638</p>
                  <p className="text-xs text-gray-500">E-Mail: cahyderali@gmail.com</p>
                </div>

                {/* Invoice Title */}
                <h3 className="text-center text-lg font-bold underline mb-4 text-blue-800">INVOICE</h3>

                {/* Invoice Info */}
                <div className="flex justify-between mb-4">
                  <div>
                    <p className="text-xs text-gray-500">Invoice No:</p>
                    <p className="font-bold text-lg">{selectedTaskForView.invoice?.number}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-500">Date:</p>
                    <p className="font-medium">{formatDate(selectedTaskForView.invoice?.date)}</p>
                  </div>
                </div>

                {/* Party Section */}
                <div className="bg-gray-50 p-3 rounded mb-4">
                  <p className="text-xs text-gray-500">Bill To:</p>
                  <p className="font-bold text-lg">{clients.find(c => c.id === selectedTaskForView.client_id)?.name || selectedTaskForView.client_name}</p>
                  <p className="text-xs text-gray-500">Service: {selectedTaskForView.service_name} - {selectedTaskForView.task_type_name || ''}</p>
                </div>

                {/* Items Table */}
                <table className="w-full mb-4 border">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="px-3 py-2 text-left text-xs font-medium border w-12">SI No.</th>
                      <th className="px-3 py-2 text-left text-xs font-medium border">Description</th>
                      <th className="px-3 py-2 text-right text-xs font-medium border w-28">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="px-3 py-2 text-sm border">1</td>
                      <td className="px-3 py-2 text-sm border">{selectedTaskForView.invoice?.description || selectedTaskForView.service_name + ' - ' + (selectedTaskForView.task_type_name || '')}</td>
                      <td className="px-3 py-2 text-sm text-right font-medium border">₹{(selectedTaskForView.invoice?.amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                    </tr>
                    {/* Reimbursements */}
                    {selectedTaskForView.reimbursements?.map((reimb, idx) => (
                      <tr key={reimb.id || idx} className="bg-amber-50">
                        <td className="px-3 py-2 text-sm border">{idx + 2}</td>
                        <td className="px-3 py-2 text-sm border">{reimb.type}{reimb.description ? ' - ' + reimb.description : ''}</td>
                        <td className="px-3 py-2 text-sm text-right font-medium border">₹{(reimb.amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Subtotals if reimbursements exist */}
                {selectedTaskForView.reimbursements?.length > 0 && (
                  <div className="mb-4 text-sm">
                    <div className="flex justify-between py-1 border-b border-dashed">
                      <span className="text-gray-500">Professional Fees:</span>
                      <span className="font-medium">₹{(selectedTaskForView.invoice?.amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                    </div>
                    <div className="flex justify-between py-1 border-b border-dashed">
                      <span className="text-gray-500">Reimbursements (Tax/Govt Fees):</span>
                      <span className="font-medium">₹{(selectedTaskForView.reimbursements?.reduce((sum, r) => sum + (r.amount || 0), 0) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                    </div>
                  </div>
                )}

                {/* Total */}
                <div className="bg-blue-50 p-4 rounded text-right mb-4">
                  <p className="text-sm text-gray-600">Grand Total:</p>
                  <p className="text-2xl font-bold text-blue-800">
                    ₹{((selectedTaskForView.invoice?.amount || 0) + (selectedTaskForView.reimbursements?.reduce((sum, r) => sum + (r.amount || 0), 0) || 0)).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </p>
                  <p className="text-xs italic text-gray-500">
                    INR {numberToWords(Math.round((selectedTaskForView.invoice?.amount || 0) + (selectedTaskForView.reimbursements?.reduce((sum, r) => sum + (r.amount || 0), 0) || 0)))}
                  </p>
                </div>

                {/* Bank Details */}
                <div className="bg-gray-50 p-3 rounded mb-4 text-sm">
                  <p className="font-bold mb-2">Bank Details:</p>
                  <div className="grid grid-cols-2 gap-1 text-xs">
                    <span className="text-gray-500">Bank Name:</span>
                    <span>BANK OF BARODA</span>
                    <span className="text-gray-500">A/c No.:</span>
                    <span>40900200000273</span>
                    <span className="text-gray-500">Branch & IFS Code:</span>
                    <span>KOTTAKKAL & BARBOKOTTAK</span>
                  </div>
                </div>

                {/* Signature */}
                <div className="flex justify-between items-end">
                  <p className="text-xs text-gray-400">E. & O.E</p>
                  <div className="text-right">
                    <p className="text-sm font-medium">for HYDER ALI AND ASSOCIATES</p>
                    <div className="border-t border-gray-400 pt-2 w-48 mt-8">
                      <p className="text-sm">Authorised Signatory</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reimbursement Modal */}
      {showReimbursementModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4 flex items-center">
              <Banknote className="w-5 h-5 mr-2 text-amber-500" />
              Add Reimbursement (Tax/Govt Fee)
            </h3>
            <form onSubmit={handleAddReimbursement} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Type *</label>
                <select
                  value={reimbursementForm.type}
                  onChange={(e) => setReimbursementForm({ ...reimbursementForm, type: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  required
                >
                  <option value="Govt Fee">Govt Fee</option>
                  <option value="Tax">Tax</option>
                  <option value="Stamp Duty">Stamp Duty</option>
                  <option value="Filing Fee">Filing Fee</option>
                  <option value="Challan">Challan</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <input
                  type="text"
                  value={reimbursementForm.description}
                  onChange={(e) => setReimbursementForm({ ...reimbursementForm, description: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="e.g., MCA Filing Fee, ROC Fee..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Amount *</label>
                <input
                  type="number"
                  step="0.01"
                  value={reimbursementForm.amount}
                  onChange={(e) => setReimbursementForm({ ...reimbursementForm, amount: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="Enter amount"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Date *</label>
                <input
                  type="date"
                  value={reimbursementForm.date}
                  onChange={(e) => setReimbursementForm({ ...reimbursementForm, date: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  required
                />
              </div>
              <div className="flex space-x-2">
                <button
                  type="submit"
                  className="bg-amber-500 text-white px-4 py-2 rounded-lg hover:bg-amber-600"
                >
                  Add Reimbursement
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowReimbursementModal(false);
                    setSelectedTaskForReimbursement(null);
                  }}
                  className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Receipt View Modal */}
      {showViewReceiptModal && selectedTaskForReceiptView && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b bg-teal-50 sticky top-0">
              <h3 className="text-lg font-semibold flex items-center">
                <Receipt className="w-5 h-5 mr-2 text-teal-600" />
                Payment Receipt
              </h3>
              <div className="flex items-center space-x-2">
                <button
                  onClick={handlePrintReceipt}
                  className="flex items-center px-3 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700"
                >
                  <Printer className="w-4 h-4 mr-2" />
                  Print Receipt
                </button>
                <button
                  onClick={() => {
                    setShowViewReceiptModal(false);
                    setSelectedTaskForReceiptView(null);
                  }}
                  className="text-gray-500 hover:text-gray-700 p-2"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Receipt Preview */}
            <div className="p-6">
              <div className="border-2 border-gray-300 rounded-lg p-6">
                {/* Header */}
                <div className="text-center border-b pb-4 mb-4">
                  <h2 className="text-xl font-bold text-blue-900">HYDER ALI AND ASSOCIATES</h2>
                  <p className="text-sm font-medium text-gray-700">CHARTERED ACCOUNTANTS</p>
                  <p className="text-xs text-gray-500">XI/405,406,407, Sahara Building, Kadappadi, Peruvallur, Kondotty Malappuram, Kerala- 673638</p>
                  <p className="text-xs text-gray-500">E-Mail: cahyderali@gmail.com</p>
                </div>

                <h3 className="text-center text-lg font-bold underline mb-4">PAYMENT RECEIPT</h3>

                {/* Info */}
                <div className="flex justify-between mb-4">
                  <div>
                    <p className="text-xs text-gray-500">Date:</p>
                    <p className="font-medium">{new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
                  </div>
                  {selectedTaskForReceiptView.invoice && (
                    <div className="text-right">
                      <p className="text-xs text-gray-500">Invoice Ref:</p>
                      <p className="font-medium">{selectedTaskForReceiptView.invoice.number}</p>
                    </div>
                  )}
                </div>

                {/* Party */}
                <div className="bg-gray-50 p-3 rounded mb-4">
                  <p className="text-xs text-gray-500">Received From:</p>
                  <p className="font-bold text-lg">{clients.find(c => c.id === selectedTaskForReceiptView.client_id)?.name || selectedTaskForReceiptView.client_name}</p>
                  <p className="text-xs text-gray-500">Service: {selectedTaskForReceiptView.service_name} - {selectedTaskForReceiptView.task_type_name || ''}</p>
                </div>

                {/* Receipts Table */}
                <table className="w-full mb-4 border">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="px-3 py-2 text-left text-xs font-medium border">SI No.</th>
                      <th className="px-3 py-2 text-left text-xs font-medium border">Date</th>
                      <th className="px-3 py-2 text-left text-xs font-medium border">Receipt No.</th>
                      <th className="px-3 py-2 text-right text-xs font-medium border">Amount</th>
                      <th className="px-3 py-2 text-left text-xs font-medium border">Mode</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedTaskForReceiptView.receipts?.map((r, idx) => (
                      <tr key={idx}>
                        <td className="px-3 py-2 text-sm border">{idx + 1}</td>
                        <td className="px-3 py-2 text-sm border">{formatDate(r.date)}</td>
                        <td className="px-3 py-2 text-sm border">{r.number || '-'}</td>
                        <td className="px-3 py-2 text-sm text-right font-medium border">₹{(r.amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                        <td className="px-3 py-2 text-sm border">{r.mode || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Total */}
                <div className="bg-green-50 p-4 rounded text-right mb-4">
                  <p className="text-sm text-gray-600">Total Amount Received:</p>
                  <p className="text-2xl font-bold text-green-700">
                    ₹{(selectedTaskForReceiptView.receipts?.reduce((sum, r) => sum + (r.amount || 0), 0) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </p>
                  <p className="text-xs italic text-gray-500">
                    INR {numberToWords(Math.round(selectedTaskForReceiptView.receipts?.reduce((sum, r) => sum + (r.amount || 0), 0) || 0))}
                  </p>
                </div>

                {/* Thanks */}
                <div className="bg-orange-50 p-4 rounded text-center mb-4">
                  <p className="text-lg font-medium text-orange-700">Thank you for your payment!</p>
                </div>

                {/* Signature */}
                <div className="flex justify-end">
                  <div className="text-right">
                    <div className="border-t border-gray-400 pt-2 w-48">
                      <p className="text-sm">Authorised Signatory</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Billing;
